import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { formatNumber } from '@bookingdei/i18n/format-number';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import { brand } from '../theme/tokens';
import { brandAssets } from '../theme/brand-assets';
import { useLanguage } from './language-context';
import { useSearchHint } from './search-hint';
import { Icon } from './icon';
import { navItemForPath } from './nav-items';
import type { ShellBadges } from './nav-items';
import { LanguageToggle } from './language-toggle';
import { ThemeToggle } from './theme-toggle';
import { OfflineChip } from './offline-chip';
import { CommandPalette } from './command-palette';
import { NotificationTray, useNotificationFeed } from './notification-tray';
import { AccountMenu } from './account-menu';
import { Avatar } from './avatar';
import type { User } from '../mock/seeds/schema-types';
import './top-bar.css';
import { formatPhone } from '@bookingdei/i18n/phone';
import { phonePlan } from '../theme/phone-plan';
import { useTextOverflow } from './use-text-overflow';

// Desktop + mobile top bar (CB-17; Others.dc.html L108-289). Desktop (≥760 px): home
// link with the screen's icon, eyebrow + screen title, the ⌘K search pill, language,
// theme, the bell (unread count, opens the tray) + help "?", and the account button
// (name + phone from 1120 px, avatar only below). Mobile: brand mark, eyebrow + title,
// search icon, language, theme, bell. At most one of palette / tray / menu is open.
// ⌘K / Ctrl+K toggles the palette and "/" opens it outside text fields. The retired
// /notifications route lands here with `state.tray` set and opens the tray (DR-38).

type Overlay = 'palette' | 'tray' | 'menu' | null;

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable;
}

export function TopBar({
  user,
  badges,
  onSignOut,
}: {
  user: User | undefined;
  badges: ShellBadges;
  onSignOut: () => void;
}) {
  const { t, lang } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [overlay, setOverlay] = useState<Overlay>(null);
  const overlayRef = useRef<Overlay>(null);
  overlayRef.current = overlay;
  const feed = useNotificationFeed(location.pathname);
  const close = () => setOverlay(null);
  const toggle = (which: Exclude<Overlay, null>) => setOverlay((cur) => (cur === which ? null : which));

  // /notifications → "/" with the tray open (router: retired route). Consume the flag
  // so a reload or Back does not reopen it.
  const trayRequested = (location.state as { tray?: boolean } | null)?.tray === true;
  useEffect(() => {
    if (!trayRequested) return;
    setOverlay('tray');
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [trayRequested, navigate, location.pathname, location.search]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOverlay((cur) => (cur === 'palette' ? null : 'palette'));
        return;
      }
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey && !isTyping(e.target) && overlayRef.current === null) {
        e.preventDefault();
        setOverlay('palette');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const searchHint = useSearchHint();
  // M4: squeezed between the title and the controls (tablet), the pill cannot show the
  // full hint — it reads just "Search" and names the full hint as its accessible name.
  const searchTextRef = useRef<HTMLSpanElement>(null);
  const searchNarrow = useTextOverflow(searchTextRef, searchHint);

  const current = navItemForPath(location.pathname);
  const title = current ? t(current.key) : t('user.shell.role');
  const unreadLabel = formatMessage(lang, 'user.shell.notif.unread', { n: feed.unread });

  return (
    <header className="top-bar">
      <Link to="/" className="top-bar__mark" aria-label={brand.name}>
        <img src={brandAssets.mark} alt="" width={28} height={28} />
      </Link>
      <Link to="/" className="top-bar__home" aria-label={t('user.shell.nav.home')}>
        <Icon name={current?.icon ?? 'home'} size={17} />
      </Link>
      <div className="top-bar__titles">
        <span className="top-bar__eyebrow">{t('user.shell.role')}</span>
        <span className="top-bar__title">{title}</span>
      </div>
      <OfflineChip />

      <div className="top-bar__search-wrap">
        <button
          type="button"
          className="top-bar__search"
          onClick={() => setOverlay('palette')}
          aria-keyshortcuts="Meta+K Control+K /"
          aria-label={searchNarrow ? searchHint : undefined}
        >
          <Icon name="search" size={18} stroke={1.7} />
          <span className="top-bar__search-text" ref={searchTextRef}>{searchNarrow ? t('user.shell.search.placeholder_short') : searchHint}</span>
          <span className="top-bar__kbd" aria-hidden="true">
            {t('user.shell.search.shortcut')}
          </span>
        </button>
      </div>

      <div className="top-bar__actions">
        <button type="button" className="top-bar__round top-bar__search-icon" aria-label={t('user.shell.search.open')} onClick={() => setOverlay('palette')}>
          <Icon name="search" size={18} stroke={1.7} />
        </button>
        <LanguageToggle />
        <ThemeToggle />
        <div className="top-bar__bell-group">
          <div className="top-bar__bell-wrap">
            <button
              type="button"
              className="top-bar__bell"
              aria-label={feed.unread > 0 ? `${t('user.shell.notif.title')} — ${unreadLabel}` : t('user.shell.notif.title')}
              aria-haspopup="dialog"
              aria-expanded={overlay === 'tray'}
              onClick={() => toggle('tray')}
            >
              <Icon name="bell" size={18} stroke={1.7} />
            </button>
            {feed.unread > 0 && (
              <span className="top-bar__bell-count" aria-hidden="true">
                {formatNumber(lang, feed.unread)}
              </span>
            )}
          </div>
          <span className="top-bar__divider" aria-hidden="true" />
          <Link to="/help" className="top-bar__help" aria-label={t('user.shell.nav.help')}>
            ?
          </Link>
        </div>
        <button
          type="button"
          className={overlay === 'menu' ? 'top-bar__account is-open' : 'top-bar__account'}
          aria-haspopup="menu"
          aria-expanded={overlay === 'menu'}
          aria-label={`${t('user.shell.account.menu')}: ${user?.name ?? ''}`}
          onClick={() => toggle('menu')}
        >
          <Avatar user={user} size={32} />
          <span className="top-bar__who">
            <span className="top-bar__who-name">{user?.name}</span>
            <span className="top-bar__who-phone">{formatPhone(user?.phone, phonePlan)}</span>
          </span>
          <span className="top-bar__chev">
            <Icon name="chevronDown" size={16} stroke={2.1} />
          </span>
        </button>
      </div>

      <CommandPalette open={overlay === 'palette'} onClose={close} />
      <NotificationTray open={overlay === 'tray'} onClose={close} feed={feed} />
      <AccountMenu open={overlay === 'menu'} onClose={close} user={user} badges={badges} onSignOut={onSignOut} />
    </header>
  );
}
